import React, { createContext, useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { getAccountDetails, getCurrentBalance } from '../api/settings';

export const StoreContext = createContext(null);

const StoreProvider = ({ children }) => {
  // Define state variables and their setters for various options and data

  const [accountDetails, setAccountDetails] = useState({});
  const [userDetails, setUserDetails] = useState({});
  const [traderDetails, setTraderDetails] = useState({});
  const [userRoles, setUserRoles] = useState([]);

  const [shipmentData, setShipmentData] = useState([]);
  const [shipmentStatus, setShipmentStatus] = useState([]);
  const [shipmentStatusFilter, setShipmentStatusFilter] = useState({});
  const [shipmentStatusFilterData, setShipmentStatusFilterData] = useState({});
  const [shipmentStatusData, setShipmentStatusData] = useState([]);
  const [companyList, setCompanyList] = useState([]);
  const [eoriCompany, setEoriCompany] = useState('');
  const [shipmentTotalCount, setShipmentTotalCount] = useState(0);

  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const [errorSnackbarMessage, setErrorSnackbarMessage] = useState('');

  const [maintenanceBannerMessage, setMaintenanceBannerMessage] = useState('');
  const [maintenancePageMessage, setMaintenancePageMessage] = useState('');
  const [maintenanceRefresh, setMaintenanceRefresh] = useState(false);

  const [companyId, setCompanyId] = useState(sessionStorage.getItem('companyid'));
  const [companyType, setCompanyType] = useState(null);
  const [currentBalance, setCurrentBalance] = useState(0);
  const [hasSufficientBalance, setHasSufficientBalance] = useState(true);

  const [isLoadingSSO, setIsLoadingSSO] = useState(false);
  const [isSSOUserWithNoCompany, setIsSSOUserWithNoCompany] = useState(false);
  const [ssoUserDetails, setSSOUserDetails] = useState({});

  const [dashboardFilters, setDashboardFilters] = useState({
    clientJobNumber: '',
    vehicleIdentity: '',
    lrnMRN: '',
    status: '',
    companyId: null,
    reference: '',
    consignmentRef: '',
    departureStartDate: null,
    departureEndDate: null,
    arrivalStartDate: null,
    arrivalEndDate: null,
  });

  const [dashboardState, setDashboardState] = useState({});

  useEffect(() => {
    const fetchCompanyTypeAndBalance = async () => {
      const account = await getAccountDetails(companyId);
      const balance = await getCurrentBalance(companyId);

      if (account?.customerTypeId) {
        setCompanyType(account.customerTypeId);
      }

      if (balance?.currentBalance) {
        setCurrentBalance(balance.currentBalance);
      }
    };

    if (companyId) {
      fetchCompanyTypeAndBalance();
    }
  }, [companyId]);

  const showError = (message) => {
    setErrorSnackbarMessage(message);
    setShowErrorSnackbar(true);
  };

  const isICS2DirectCompany = () => {
    // 10 = ICS2 Direct, 60 = 360PAY Subscription
    return companyType === 10 || companyType === 60;
  };

  useEffect(() => {
    // if customer type is ICS2 Direct, then user must have some balance to create ENS
    const noBalance = (companyType === 10 || companyType === 60) && currentBalance <= 0;
    setHasSufficientBalance(!noBalance);
  }, [companyType, currentBalance]);

  const store = useMemo(
    () => ({
      accountDetails: [accountDetails, setAccountDetails],
      userDetails: [userDetails, setUserDetails],
      traderDetails: [traderDetails, setTraderDetails],
      userRoles: [userRoles, setUserRoles],

      shipmentData: [shipmentData, setShipmentData],
      shipmentStatus: [shipmentStatus, setShipmentStatus],
      shipmentStatusFilter: [shipmentStatusFilter, setShipmentStatusFilter],
      shipmentStatusFilterData: [shipmentStatusFilterData, setShipmentStatusFilterData],
      shipmentStatusData: [shipmentStatusData, setShipmentStatusData],
      companyList: [companyList, setCompanyList],
      eoriCompany: [eoriCompany, setEoriCompany],
      shipmentTotalCount: [shipmentTotalCount, setShipmentTotalCount],
      maintenanceBanner: [maintenanceBannerMessage, setMaintenanceBannerMessage],
      maintenancePage: [maintenancePageMessage, setMaintenancePageMessage],
      maintenanceRefresh: [maintenanceRefresh, setMaintenanceRefresh],
      showErrorSnackbar: [showErrorSnackbar, setShowErrorSnackbar],
      errorSnackbarMessage: [errorSnackbarMessage, setErrorSnackbarMessage],
      showError,
      hideError: () => setShowErrorSnackbar(false),
      dashboardFilters: [dashboardFilters, setDashboardFilters],
      dashboardState: [dashboardState, setDashboardState],

      companyId: [companyId, setCompanyId],
      companyType: [companyType, setCompanyType],
      currentBalance: [currentBalance, setCurrentBalance],
      hasSufficientBalance,
      isICS2DirectCompany,
      isLoadingSSO: [isLoadingSSO, setIsLoadingSSO],
      ssoUserDetails: [ssoUserDetails, setSSOUserDetails],
      isSSOUserWithNoCompany: [isSSOUserWithNoCompany, setIsSSOUserWithNoCompany],
    }),
    [
      accountDetails,
      setAccountDetails,
      userDetails,
      setUserDetails,
      traderDetails,
      setTraderDetails,
      userRoles,
      setUserRoles,

      shipmentData,
      setShipmentData,
      shipmentStatus,
      setShipmentStatus,
      shipmentStatusFilter,
      setShipmentStatusFilter,
      shipmentStatusFilterData,
      setShipmentStatusFilterData,
      shipmentStatusData,
      setShipmentStatusData,
      companyList,
      setCompanyList,
      eoriCompany,
      setEoriCompany,
      shipmentTotalCount,
      setShipmentTotalCount,

      showErrorSnackbar,
      setShowErrorSnackbar,
      errorSnackbarMessage,
      setErrorSnackbarMessage,
      showError,
      setShowErrorSnackbar,

      maintenanceBannerMessage,
      setMaintenanceBannerMessage,
      maintenancePageMessage,
      setMaintenancePageMessage,
      maintenanceRefresh,
      setMaintenanceRefresh,
      dashboardFilters,
      setDashboardFilters,
      dashboardState,
      setDashboardState,

      companyId,
      companyType,
      setCompanyType,
      currentBalance,
      setCurrentBalance,
      hasSufficientBalance,
      isICS2DirectCompany,
      isLoadingSSO,
      setIsLoadingSSO,
      ssoUserDetails,
      setSSOUserDetails,
      isSSOUserWithNoCompany,
      setIsSSOUserWithNoCompany,
    ]
  );

  return <StoreContext.Provider value={store}>{children}</StoreContext.Provider>;
};

export default StoreProvider;
StoreProvider.propTypes = {
  children: PropTypes.node,
};
